import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { StarRating } from "@/components/ui/star-rating";
import {
  supplierService,
  UpdateSupplierRequest,
  ESupplierStatus,
  Supplier,
} from "@/services/supplier";

interface UpdateSupplierModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier: Supplier;
}

export function UpdateSupplierModal({
  open,
  onOpenChange,
  supplier,
}: UpdateSupplierModalProps) {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<UpdateSupplierRequest>({
    name: supplier.name,
    phone: supplier.phone ?? "",
    email: supplier.email ?? "",
    notes: supplier.notes ?? "",
    status: supplier.status,
    score: supplier.score ?? undefined,
  });

  useEffect(() => {
    if (open) {
      setFormData({
        name: supplier.name,
        phone: supplier.phone ?? "",
        email: supplier.email ?? "",
        notes: supplier.notes ?? "",
        status: supplier.status,
        score: supplier.score ?? undefined,
      });
    }
  }, [open, supplier]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const updateMutation = useMutation({
    mutationFn: (data: UpdateSupplierRequest) =>
      supplierService.update(supplier.id, data),
    onSuccess: () => {
      toast.success("Proveedor actualizado exitosamente");
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["supplier", supplier.id] });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = () => {
    if (!formData.name?.trim()) {
      toast.error("El nombre del proveedor es requerido");
      return;
    }
    updateMutation.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Actualizar proveedor</DialogTitle>
          <DialogDescription>
            Modifica los datos del proveedor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              Nombre <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Proveedor Principal"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Teléfono</Label>
            <Input
              id="phone"
              placeholder="+573001234567"
              value={formData.phone}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, phone: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="proveedor@example.com"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label>Puntuación</Label>
            <div className="flex items-center gap-3">
              <StarRating
                size="lg"
                value={formData.score ?? 0}
                onChange={(score) =>
                  setFormData((prev) => ({ ...prev, score }))
                }
              />
              <span className="text-sm text-muted-foreground">
                {formData.score ?? 0} / 10
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              placeholder="Información adicional sobre el proveedor..."
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Estado</Label>
            <Select
              value={formData.status}
              onValueChange={(val) =>
                setFormData((prev) => ({
                  ...prev,
                  status: val as ESupplierStatus,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ESupplierStatus.ACTIVE}>Activo</SelectItem>
                <SelectItem value={ESupplierStatus.INACTIVE}>
                  Inactivo
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={updateMutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
